import React, { useState, useEffect, useContext } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Spinner, Alert, Card, Table } from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment';
import ProjectContext from '../context/ProjectContext';
import AuthContext from '../context/AuthContext';

// Components
import FilterSection from '../components/transactions/FilterSection';
import TransactionRow from '../components/transactions/TransactionRow';
import DataTable from '../components/common/DataTable';
import PaginationSection from '../components/transactions/PaginationSection';
import { RegisterHeaderSummary } from '../components/transactions/RegisterHeaderSummary';

// Hooks
import { useTransactions } from '../hooks/useTransactions';
import { formatCurrency } from '../utils/formatters';

const AccountTransactions = () => {
  const { accountId } = useParams();
  const location = useLocation();
  const { activeProject } = useContext(ProjectContext);
  const { user } = useContext(AuthContext);
  const history = useHistory();

  // Local master states for selectors
  const [account, setAccount] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [payees, setPayees] = useState([]);
  const [classes, setClasses] = useState([]);
  const [masterLoading, setMasterLoading] = useState(true);

  // Selected state for row checkboxes
  const [selectedIds, setSelectedIds] = useState([]);

  // Fetch specific account details to refresh current balance
  const fetchAccountDetails = async () => {
    if (!activeProject?._id || !accountId) return;
    try {
      const res = await axios.get(`/api/projects/${activeProject._id}/accounts`);
      setAccounts(res.data);
      const matchedAcc = res.data.find(a => a._id === accountId);
      if (matchedAcc) {
        setAccount(matchedAcc);
      }
    } catch (err) {
      console.error('Error fetching account details:', err);
    }
  };

  // Use unified useTransactions hook
  const {
    transactions, loading: txLoading, error,
    page, setPage, limit, setLimit, total, pages,
    startDate, setStartDate,
    endDate, setEndDate,
    categoryFilter, setCategoryFilter,
    payeeFilter, setPayeeFilter,
    classFilter, setClassFilter,
    descriptionFilter, setDescriptionFilter,
    onlyUncategorized, setOnlyUncategorized,
    postingFilter, setPostingFilter,
    fetchTransactions,
    updateTransaction,
    deleteTransaction,
    setAccountIdFilter,
    beginningBalance,
    endingBalance,
    sortBy,
    sortOrder,
    handleSort
  } = useTransactions(activeProject, false, fetchAccountDetails);

  const projectMember = activeProject?.members?.find(m => m.user === user?._id);
  const isEditor = user?.type === 'admin' || projectMember?.role === 'editor';

  // Types list
  const plTypes = ['Income', 'Expenses', 'Expense', 'Cost of Goods Sold', 'Other Income', 'Other Expense'];
  const bsTypes = ['Bank', 'Credit Card', 'Accounts receivable (A/R)', 'Accounts payable (A/P)', 'Other Current Assets', 'Other Current Liabilities', 'Fixed Assets', 'Long Term Liabilities', 'Equity'];

  // Keep accountIdFilter in sync with URL parameter and default to posted or URL parameters
  useEffect(() => {
    if (accountId) {
      setAccountIdFilter(accountId);
      
      const queryParams = new URLSearchParams(location.search);
      const urlEndDate = queryParams.get('endDate');
      const urlPosting = queryParams.get('posting');
      
      if (urlEndDate) {
        setEndDate(urlEndDate);
      } else {
        setEndDate('');
      }
      
      if (urlPosting) {
        setPostingFilter(urlPosting);
      } else {
        setPostingFilter('posted');
      }
      
      // Clear startDate since balance sheet is point-in-time
      setStartDate('');
    }
  }, [accountId, location.search, setAccountIdFilter, setPostingFilter, setEndDate, setStartDate]);

  // Fetch Master Selector Lists (Accounts, Payees, Classes)
  useEffect(() => {
    if (!activeProject?._id) return;
    const fetchMasterData = async () => {
      setMasterLoading(true);
      try {
        await fetchAccountDetails();
        const [payeesRes, classesRes] = await Promise.all([
          axios.get(`/api/projects/${activeProject._id}/payees?limit=1000`),
          axios.get(`/api/projects/${activeProject._id}/classes`)
        ]);
        setPayees(payeesRes.data.payees || []);
        setClasses(classesRes.data || []);
      } catch (err) {
        console.error('Error fetching master register data:', err);
      } finally {
        setMasterLoading(false);
      }
    };
    fetchMasterData();
  }, [activeProject?._id, accountId]);

  // Wrap mutations to also refresh the header account balance card
  const handleUpdateAndRefresh = async (txId, updates) => {
    const success = await updateTransaction(txId, updates, accounts);
    if (success) {
      fetchAccountDetails();
    }
  };

  const handleDeleteAndRefresh = async (txId) => {
    const success = await deleteTransaction(txId);
    if (success) {
      fetchAccountDetails();
    }
  };
  const getQBType = (tx) => {
    if (tx.plaidTransactionId) return 'Plaid';
    if (tx.type === 'Journal Entry') return 'Journal Entry';
    if (tx.type === 'Adjustment') return 'Adjustment';
    if (tx.type === 'Imported') return 'QB';
    
    const isCreditCard = account?.type === 'Credit Card';
    if (isCreditCard) {
      return tx.amount >= 0 ? 'CC Charge' : 'CC Payment';
    }
    return tx.amount >= 0 ? 'Deposit' : 'Check';
  };

  const getQBNum = (tx) => {
    if (!tx.description) return '';
    const match = tx.description.match(/(?:check|num|#)\s*([0-9a-zA-Z-]+)/i) || tx.description.match(/\b(\d{4,6})\b/);
    return match ? match[1] : '';
  };

  const getSplitAccountName = (tx) => {
    if (tx.type === 'Journal Entry') return 'Split';
    
    const fromId = tx.fromAccount?._id ? tx.fromAccount._id.toString() : (tx.fromAccount ? tx.fromAccount.toString() : '');
    const toId = tx.toAccount?._id ? tx.toAccount._id.toString() : (tx.toAccount ? tx.toAccount.toString() : '');
    const currentId = accountId ? accountId.toString() : '';

    let opposingId = '';
    if (fromId === currentId) {
      opposingId = toId;
    } else if (toId === currentId) {
      opposingId = fromId;
    } else {
      return 'Split';
    }

    if (!opposingId) return 'Uncategorized';

    const opposingAcc = accounts.find(a => a._id.toString() === opposingId);
    return opposingAcc ? opposingAcc.name : 'Uncategorized';
  };

  // Add custom helper creation functions
  const addPayee = async (projectId, name) => {
    try {
      const res = await axios.post(`/api/projects/${projectId}/payees`, { name });
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.msg || 'Error adding payee' };
    }
  };

  const addClass = async (projectId, name) => {
    try {
      const res = await axios.post(`/api/projects/${projectId}/classes`, { name });
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.msg || 'Error adding class' };
    }
  };

  const addAccount = async (payload) => {
    try {
      const res = await axios.post(`/api/projects/${activeProject._id}/accounts`, payload);
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.msg || 'Error adding category' };
    }
  };

  if (!activeProject) return <div className="p-5 text-center">Please select a project first.</div>;

  return (
    <div className="transactions-container pb-5">
      <RegisterHeaderSummary
        history={history}
        account={account}
        formatCurrency={formatCurrency}
        balanceOverride={endDate ? (account?.type === 'Credit Card' ? endingBalance : endingBalance) : undefined}
        endDate={endDate}
      />

      {error && <Alert variant="danger" className="shadow-sm">{error}</Alert>}

      <Card className="shadow-sm border-0">
        <div className="table-responsive">
          <Table hover className="one-line-table mb-0 report-table table-sm">
            <thead className="thead-light">
              <tr className="small text-uppercase text-muted font-weight-bold">
                <th 
                  onClick={() => handleSort('date')} 
                  style={{ width: '90px', cursor: 'pointer' }}
                >
                  Date {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  onClick={() => handleSort('type')} 
                  style={{ width: '110px', cursor: 'pointer' }}
                >
                  Type {sortBy === 'type' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ width: '80px' }}>Num</th>
                <th 
                  onClick={() => handleSort('payee')} 
                  style={{ cursor: 'pointer' }}
                >
                  Name {sortBy === 'payee' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  onClick={() => handleSort('description')} 
                  style={{ cursor: 'pointer' }}
                >
                  Description {sortBy === 'description' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th>Full Name</th>
                <th>Split Account</th>
                <th 
                  onClick={() => handleSort('amount')} 
                  className="text-right" 
                  style={{ width: '110px', cursor: 'pointer' }}
                >
                  Amount {sortBy === 'amount' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="text-right" style={{ width: '120px' }}>Balance</th>
              </tr>
            </thead>
            <tbody>
              {txLoading ? (
                <tr>
                  <td colSpan="9" className="text-center py-5">
                    <Spinner animation="border" variant="primary" size="sm" className="mr-2" />
                    <span className="text-muted small">Loading transactions...</span>
                  </td>
                </tr>
              ) : (
                <>
                  {/* Top static balance row (only show on page 1) */}
                  {page === 1 && (
                    sortOrder === 'desc' ? (
                      <tr className="extra-small font-weight-bold bg-light">
                        <td>{endDate ? moment.utc(endDate).format('MM/DD/YYYY') : (transactions.length > 0 ? moment.utc(transactions[0].date).format('MM/DD/YYYY') : '')}</td>
                        <td>Ending Balance</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td className="text-right">{formatCurrency(endingBalance)}</td>
                      </tr>
                    ) : (
                      <tr className="extra-small font-weight-bold bg-light">
                        <td>{startDate ? moment.utc(startDate).format('MM/DD/YYYY') : (transactions.length > 0 ? moment.utc(transactions[transactions.length - 1].date).format('MM/DD/YYYY') : '')}</td>
                        <td>Beginning Balance</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td className="text-right">{formatCurrency(beginningBalance)}</td>
                      </tr>
                    )
                  )}
                  {transactions.length > 0 ? (
                    transactions.map((tx) => (
                      <tr key={tx._id} className="extra-small">
                        <td>{tx.date ? moment.utc(tx.date).format('MM/DD/YYYY') : 'N/A'}</td>
                        <td>{getQBType(tx)}</td>
                        <td>{getQBNum(tx)}</td>
                        <td>{tx.payeeId ? tx.payeeId.name : ''}</td>
                        <td title={tx.description}>{tx.description}</td>
                        <td>{tx.payeeId ? tx.payeeId.name : ''}</td>
                        <td>{getSplitAccountName(tx)}</td>
                        <td className={`text-right font-weight-bold ${tx.amount >= 0 ? 'text-success' : 'text-danger'}`}>
                          {formatCurrency(tx.amount)}
                        </td>
                        <td className="text-right font-weight-bold">
                          {formatCurrency(tx.runningBalance)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-5 text-muted">
                        No transactions found matching your filters.
                      </td>
                    </tr>
                  )}
                  {/* Bottom static balance row (only show on page 1) */}
                  {page === 1 && (
                    sortOrder === 'desc' ? (
                      <tr className="extra-small font-weight-bold bg-light border-top">
                        <td>{startDate ? moment.utc(startDate).format('MM/DD/YYYY') : (transactions.length > 0 ? moment.utc(transactions[transactions.length - 1].date).format('MM/DD/YYYY') : '')}</td>
                        <td>Beginning Balance</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td className="text-right">{formatCurrency(beginningBalance)}</td>
                      </tr>
                    ) : (
                      <tr className="extra-small font-weight-bold bg-light border-top">
                        <td>{endDate ? moment.utc(endDate).format('MM/DD/YYYY') : (transactions.length > 0 ? moment.utc(transactions[0].date).format('MM/DD/YYYY') : '')}</td>
                        <td>Ending Balance</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td className="text-right">{formatCurrency(endingBalance)}</td>
                      </tr>
                    )
                  )}
                </>
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      {!accountId && <PaginationSection page={page} pages={pages} setPage={setPage} />}
    </div>
  );
};

export default AccountTransactions;
